import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Button
} from '@mui/material';
import { useUser } from './UserContext';
import CustomAppBar from './CustomAppBar';
import { httpApi } from './api';

const Dashboard = () => {
  const { user } = useUser();
  const [formData, setFormData] = useState({
    user_id: '',
    initial_stop_loss_percentage: 0,
    pnl_max_threshold: 0,
    pnl_min_threshold: 0,
    trailing_step_increment_pct: 0,
    trailing_step_threshold_pct: 0,
    trailing_stop_loss_percentage: 0,
    trailing_trigger_percentage: 0,
    buffer_percentage: 0,
    target_percentage: 0
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userIdResponse = await httpApi.get(
          `/api/userid-by-email?emailid=${encodeURIComponent(user.email)}`
        );
        const userId = userIdResponse.data.userid;

        const paramsResponse = await httpApi.get(
          `/api/trailing-stop-parameters?userid=${userId}`
        );
        
        setFormData({
          ...paramsResponse.data,
          user_id: userId
        });

      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    if (user.email) {
      fetchUserData();
    }
  }, [user.email]);

  const handleLoadTokens = async () => {
    const dataToSend = { user_id: formData.user_id };
    try {
      await httpApi.post('/api/load_instruments', dataToSend);
    } catch (error) {
      console.error('Error loading tokens:', error);
    }
  };

  const handleKillSwitch = async () => {
    const dataToSend = { user_id: formData.user_id };
    try {
      await httpApi.post('/restart-zatamap', dataToSend);
    } catch (error) {
      console.error('Error restarting service:', error);
      throw error; // Re-throw to handle in calling function
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create payload with only required fields
      const payload = {
        pnl_min_threshold: formData.pnl_min_threshold,
        pnl_max_threshold: formData.pnl_max_threshold,
        initial_stop_loss_percentage: formData.initial_stop_loss_percentage,
        trailing_trigger_percentage: formData.trailing_trigger_percentage,
        trailing_step_threshold_pct: formData.trailing_step_threshold_pct,
        trailing_step_increment_pct: formData.trailing_step_increment_pct,
      };

      // Update parameters
      await httpApi.put(
        `/api/trailing-stop-parameters/${formData.user_id}`,
        payload
      );
      
      // Restart service after successful update
      await handleKillSwitch();
      
    } catch (error) {
      console.error('Error updating parameters:', error);
      // Add error handling UI feedback here
    }
  };

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="sm" sx={{ mt: 4, mx: 'auto' }}>
        <form onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              name="user_id"
              label="User ID"
              value={formData.user_id}
              disabled
              fullWidth
            />

            <TextField
              name="pnl_min_threshold"
              label="PNL Min Threshold"
              type="number"
              inputProps={{ step: "0.01" }}
              value={formData.pnl_min_threshold}
              onChange={handleInputChange}
              fullWidth
            />

            <TextField
              name="pnl_max_threshold"
              label="PNL Max Threshold"
              type="number"
              inputProps={{ step: "0.01" }}
              value={formData.pnl_max_threshold}
              onChange={handleInputChange}
              fullWidth
            />

            <TextField
              name="initial_stop_loss_percentage"
              label="Initial Stop Loss (%)"
              type="number"
              inputProps={{ step: "0.01" }}
              value={formData.initial_stop_loss_percentage}
              onChange={handleInputChange}
              fullWidth
            />

            <TextField
              name="trailing_trigger_percentage"
              label="Trailing Trigger (%)"
              type="number"
              inputProps={{ step: "0.01" }}
              value={formData.trailing_trigger_percentage}
              onChange={handleInputChange}
              fullWidth
            />

            <TextField
              name="trailing_step_threshold_pct"
              label="Trailing Step Threshold (%)"
              type="number"
              inputProps={{ step: "0.01" }}
              value={formData.trailing_step_threshold_pct}
              onChange={handleInputChange}
              fullWidth
            />

            <TextField
              name="trailing_step_increment_pct"
              label="Trailing Step Increment (%)"
              type="number"
              inputProps={{ step: "0.01" }}
              value={formData.trailing_step_increment_pct}
              onChange={handleInputChange}
              fullWidth
            />

            <Box display="flex" justifyContent="space-between" gap={2}>
              <Button type="submit" variant="contained" color="primary" sx={{ flexGrow: 1 }}>
                Update & Restart
              </Button>
              <Button type="button" variant="contained" color="secondary" onClick={handleLoadTokens} sx={{ flexGrow: 1 }}>
                Load Tokens
              </Button>
              <Button type="button" variant="contained" color="error" onClick={handleKillSwitch} sx={{ flexGrow: 1 }}>
                Restart Only
              </Button>
            </Box>
          </Box>
        </form>
      </Container>
    </>
  );
};

export default Dashboard;